import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { RootState } from "src/ipm-shared/store/model/reducers";
import { history } from "src/ipm-shared/store";

import Loader from "src/bepaid/components/Loading";
import * as selectors from "src/bepaid/store/model/Fetch/selectors";
import * as actions from "src/bepaid/store/model/Fetch/actions";
import ROUTES from "../routes";
import { checkIsBusinessMyInfo } from "../utils/paymentFlow";

const mapStateToProps = (state: RootState) => ({
  isSingaporeAccount: selectors.isSingaporeAccount(state),
  isPersonalAccount: selectors.getIsPersonalAccount(state),
  isFetchedMainCollectedAccount: selectors.getIsFetchedMainCollectedAccount(
    state
  ),
  collectedAccount: selectors.getCollectedAccount(state),
  confirmedMyInfo: selectors.getConfirmedMyInfo(state)
});

const mapDispatchToProps = {
  fetchMyInfoSingPass: actions.fetchMyInfoSingPass,
  setConfirmedMyInfo: actions.setConfirmedMyInfo
};

/**
 * `handleFetchMyInfoSingPass` silently drops its callback when the request is
 * aborted by a duplicate GET, fails, or comes back with an error payload, and
 * the underlying request itself only gives up after REACT_APP_REQUEST_TIMEOUT
 * (60s). Without our own deadline the guard would sit here indefinitely, so
 * give up well before that and send the user somewhere usable.
 */
const SINGPASS_CHECK_TIMEOUT = 30000;

/**
 * Guards the payment-creation pages against users who deep-link to them (e.g.
 * `/payments/domestic/entity/select`) without going through the "Make payment"
 * button. It runs the exact same onboarding gate that button runs
 * (`onChoosePaymentType`/`handleMyinfo`): Singapore accounts without a completed
 * collected account are sent through MyInfo (carrying `redirect_url` so they
 * return here afterwards), everyone else proceeds. This keeps deep-link access
 * behaviourally identical to clicking "Make payment", so the flow can no longer
 * be bypassed.
 */
const withRequireOnboarding = (WrapperComponent: React.ComponentType) => {
  const HOC = (props: any) => {
    // The button only gates Singapore accounts; other countries proceed
    // straight to payment creation.
    const canDecide =
      !props.isSingaporeAccount || props.isFetchedMainCollectedAccount;

    const gate =
      props.isSingaporeAccount && props.isFetchedMainCollectedAccount
        ? checkIsBusinessMyInfo(props.collectedAccount, props.isPersonalAccount)
        : null;

    // The "Make payment" button runs the same SingPass check right before it
    // navigates here, storing the result in `confirmedMyInfo`. When it is
    // already confirmed we can allow synchronously and skip a second async
    // round-trip — otherwise the page would blank out (flash) while we wait for
    // the redundant SingPass response. We only need the async check for genuine
    // deep-links, where nothing set `confirmedMyInfo` yet.
    const allowedSync = canDecide && (!gate || props.confirmedMyInfo === true);

    // Block rendering until we know the user is allowed through, so the page
    // never flashes before a redirect.
    const [allowed, setAllowed] = useState(allowedSync);

    useEffect(() => {
      if (!canDecide) {
        return;
      }

      if (!gate || props.confirmedMyInfo === true) {
        setAllowed(true);
        return;
      }

      // Deep-link case: nobody has run the SingPass check yet, so verify now
      // (same check the "Make payment" button performs).
      props.fetchMyInfoSingPass(({ confirmed = false }: any) => {
        props.setConfirmedMyInfo(!!confirmed);

        if (confirmed) {
          setAllowed(true);
          return;
        }

        const redirectUrl = `${window.location.pathname}${window.location.search}`;
        history.push(
          `${ROUTES.MY_INFO}?is_biz=${
            gate.isBusiness ? "y" : "n"
          }&redirect_url=${encodeURIComponent(redirectUrl)}`
        );
      }, gate.isBusiness);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [canDecide, gate && gate.isBusiness, props.confirmedMyInfo]);

    // The SingPass check above is not guaranteed to answer, so bound how long
    // we are willing to block on it. Kept in its own effect, keyed only on
    // `allowed`, so the check effect re-running does not reset the deadline.
    useEffect(() => {
      if (allowed) {
        return;
      }

      const timer = window.setTimeout(() => {
        // Fail closed. Clicking "Make payment" degrades to a no-op when the
        // check never answers, but a deep-link has no page to stay on, so
        // bounce to the dashboard rather than leaving this route stuck.
        window.Logger.warn(
          "Ipaymy: SingPass onboarding check timed out, redirecting to root"
        );
        history.push(ROUTES.ROOT);
      }, SINGPASS_CHECK_TIMEOUT);

      return () => window.clearTimeout(timer);
    }, [allowed]);

    if (!allowed) {
      return <Loader />;
    }

    return <WrapperComponent {...props} />;
  };

  return connect(mapStateToProps, mapDispatchToProps)(HOC);
};

export default withRequireOnboarding;
